import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyStickyFooterModule } from '@vendasta/galaxy/sticky-footer';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GALAXY_UPLOADER_SERVICE_TOKEN, GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { UploadService } from './test-management-upload.service';
import { MatDialog } from '@angular/material/dialog';
import { TestUploadModalComponent, TestUploadModalResult } from './test-upload-modal/test-upload-modal.component';
import {
  TestRunOptionsModalComponent,
  TestRunOptionsModalResult,
} from './test-run-options-modal/test-run-options-modal.component';
import { MatButtonModule } from '@angular/material/button';
import {
  AssistantKey,
  AssistantType,
  AssistantVersionKey,
  getAssistant$,
  RunTestsV2Response,
  SortDirection,
  TestCaseKey,
  TestLLMOptions,
  TestType,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { TestCaseDisplayComponent } from './test-case-display/test-case-display.component';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { EMPTY, firstValueFrom, forkJoin, Observable, of, timer } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { ActivatedRoute, Router } from '@angular/router';
import { TestManagementService } from './test-management.service';
import { TestRunTableComponent } from './test-run-table/test-run-table.component';
import { AssistantNamePipe } from '../../core/utils/namespace-utils';
import { BusinessNameService } from '../../core/services/business-name.service';
import { AssistantKeyResolverService } from '../../core/services/assistant-key-resolver.service';
import { isVoiceTest, LLMOptionPair, RunVariant, snackbarDuration } from './test-run.utils';
import { MatDividerModule } from '@angular/material/divider';

@Component({
  selector: 'ai-test-management',
  imports: [
    MatButtonModule,
    CommonModule,
    GalaxyPageModule,
    GalaxyStickyFooterModule,
    MatCardModule,
    MatDividerModule,
    ReactiveFormsModule,
    AiAssistantI18nModule,
    GalaxyFormFieldModule,
    GalaxyUploaderModule,
    TestCaseDisplayComponent,
    GalaxyLoadingSpinnerModule,
    GalaxySnackbarModule,
    GalaxyAlertModule,
    TestRunTableComponent,
    AssistantNamePipe,
  ],

  providers: [
    UploadService,
    { provide: GALAXY_UPLOADER_SERVICE_TOKEN, useClass: UploadService },
    AssistantKeyResolverService,
  ],
  templateUrl: './test-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-management.component.scss',
})
export class TestManagementComponent implements OnInit {
  @ViewChild('chatTable') chatTable: TestCaseDisplayComponent;
  @ViewChild('voiceTable') voiceTable: TestCaseDisplayComponent;
  @ViewChild('testRunTable') testRunTable: TestRunTableComponent;

  chatTestType = TestType.TEST_TYPE_CHAT;
  voiceTestType = TestType.TEST_TYPE_VOICE;

  private readonly testService = inject(TestManagementService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(SnackbarService);
  private readonly businessNameService = inject(BusinessNameService);
  private activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly assistantKeyResolver = inject(AssistantKeyResolverService);
  private readonly translateService = inject(TranslateService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  testRunLoading = false;

  assistantKey: AssistantKey;
  businessName = signal<string>('');

  private assistantKey$ = this.assistantKeyResolver.resolveFromRoute(this.activatedRoute);

  protected assistant$ = this.assistantKey$.pipe(
    switchMap((key) => getAssistant$({ id: key.id, namespace: key.namespace })),
    map((resp) => resp.assistant),
    shareReplay(1),
  );

  protected isVoiceAssistant = toSignal(
    this.assistant$.pipe(map((a) => a?.type === AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST)),
    { initialValue: false },
  );

  ngOnInit(): void {
    this.assistantKey$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (assistantKey) => {
      this.assistantKey = assistantKey;

      // Load business name if this is an account group namespace
      if (assistantKey.namespace?.accountGroupNamespace?.accountGroupId) {
        const businessName = await this.businessNameService.loadBusinessName(
          assistantKey.namespace.accountGroupNamespace.accountGroupId,
        );
        this.businessName.set(businessName);
      } else {
        this.businessName.set('');
      }
    });
  }

  openUploadDialog(testType: TestType) {
    const dialogRef = this.dialog.open(TestUploadModalComponent, {
      width: '800px',
      disableClose: true,
    });
    dialogRef.componentInstance.isVoice = testType === TestType.TEST_TYPE_VOICE;
    dialogRef
      .afterClosed()
      .pipe(
        switchMap((result: TestUploadModalResult) => {
          if (result?.cases?.length > 0) {
            const upsertRequest = this.testService.upsertTestCases(this.assistantKey, result.cases);

            if (result.removeExisting) {
              return this.testService
                .listTestCasesByAssistant(
                  this.assistantKey,
                  { pageSize: 500, cursor: '' },
                  '',
                  SortDirection.SORT_DIRECTION_ASCENDING,
                )
                .pipe(
                  map((resp) =>
                    (resp.testCases || []).filter((tc) =>
                      testType === TestType.TEST_TYPE_VOICE ? isVoiceTest(tc.testType) : !isVoiceTest(tc.testType),
                    ),
                  ),
                  switchMap((cases) => {
                    const ids = cases.map((tc) => tc.id);
                    if (ids.length > 0) {
                      return this.testService.deleteTestCases(this.assistantKey, ids);
                    }
                    return of(null);
                  }),
                  switchMap(() => upsertRequest),
                );
            }
            return upsertRequest;
          }
          return EMPTY;
        }),
      )
      .subscribe({
        error: (err) => {
          const message = err.error?.message
            ? err.error.message
            : this.translateService.instant('AI_ASSISTANT.TESTING.ERROR_UPLOADING');
          this.snackBar.openErrorSnack(message, { duration: snackbarDuration });
        },
        next: () => {
          this.snackBar.openSuccessSnack(this.translateService.instant('AI_ASSISTANT.TESTING.TESTS_SAVED'), {
            duration: snackbarDuration,
          });
          this.refreshTestCases(testType);
        },
      });
  }

  async runChatTests(testCaseIDs: string[]): Promise<void> {
    const assistant = await firstValueFrom(this.assistant$);

    this.dialog
      .open(TestRunOptionsModalComponent, {
        width: '800px',
        disableClose: true,
        viewContainerRef: this.viewContainerRef,
        data: { assistant: assistant },
      })
      .afterClosed()
      .subscribe((result: TestRunOptionsModalResult) => {
        if (!result) {
          return;
        }

        let llmOptionPairs: LLMOptionPair[] = result.llmOptions.filter(
          (pair: LLMOptionPair) => pair.model !== undefined || pair.reasoningEffort,
        );
        if (llmOptionPairs.length === 0) {
          llmOptionPairs = [{}];
        }

        const runs: RunVariant[] = llmOptionPairs.map((pair) => ({ llmOptions: pair }));
        this.executeTestRuns(
          this.buildTestRunRequests(
            testCaseIDs,
            runs,
            result.iterations,
            result.runNames,
            result.assistantVersionKey,
          ),
        );
      });
  }

  async runVoiceTests(testCaseIDs: string[]): Promise<void> {
    const assistant = await firstValueFrom(this.assistant$);

    this.dialog
      .open(TestRunOptionsModalComponent, {
        width: '800px',
        disableClose: true,
        viewContainerRef: this.viewContainerRef,
        data: { assistant: assistant, voiceOnly: true },
      })
      .afterClosed()
      .subscribe((result: TestRunOptionsModalResult) => {
        if (!result) {
          return;
        }
        const runs: RunVariant[] = result.voiceConfigs.map((voiceConfig) => ({ voiceConfig, isVoice: true }));
        this.executeTestRuns(
          this.buildTestRunRequests(
            testCaseIDs,
            runs,
            result.iterations,
            result.runNames,
            result.assistantVersionKey,
          ),
        );
      });
  }

  configureAllChatTests(): void {
    this.testService
      .listTestCasesByAssistant(
        this.assistantKey,
        { pageSize: 500, cursor: '' },
        '',
        SortDirection.SORT_DIRECTION_ASCENDING,
      )
      .pipe(map((resp) => (resp.testCases || []).filter((tc) => !isVoiceTest(tc.testType)).map((tc) => tc.id)))
      .subscribe((ids) => this.runChatTests(ids));
  }

  configureAllVoiceTests(): void {
    this.testService
      .listTestCasesByAssistant(
        this.assistantKey,
        { pageSize: 500, cursor: '' },
        '',
        SortDirection.SORT_DIRECTION_ASCENDING,
      )
      .pipe(map((resp) => (resp.testCases || []).filter((tc) => isVoiceTest(tc.testType)).map((tc) => tc.id)))
      .subscribe((ids) => this.runVoiceTests(ids));
  }

  private executeTestRuns(requests: Observable<RunTestsV2Response>[]): void {
    if (requests.length === 0) {
      return;
    }

    this.testRunLoading = true;
    forkJoin(requests).subscribe({
      next: (responses) => {
        const successCount = responses.filter((r) => r.buildUrl).length;
        this.snackBar.openSuccessSnack(
          this.translateService.instant('AI_ASSISTANT.TESTING.STARTED_TEST_RUNS', { count: successCount }),
          { duration: snackbarDuration },
        );
        this.testRunLoading = false;
      },
      error: (err) => {
        const message = err.error?.message
          ? err.error.message
          : this.translateService.instant('AI_ASSISTANT.TESTING.ERROR_RUNNING_TESTS');
        this.snackBar.openErrorSnack(message, { duration: snackbarDuration });
        this.testRunLoading = false;
      },
    });
  }

  private buildTestRunRequests(
    testCaseIDs: string[],
    runs: RunVariant[],
    iterations: number,
    runNames?: string[],
    assistantVersionKey?: Partial<AssistantVersionKey>,
  ): Observable<RunTestsV2Response>[] {
    const testCaseKeys: TestCaseKey[] = testCaseIDs.map((id) => ({
      assistant: this.assistantKey,
      id,
    }));

    return runs.map((run, index) => {
      const defaultName = this.translateService.instant(
        run.isVoice
          ? 'AI_ASSISTANT.TESTING.DEFAULT_VOICE_TEST_RUN_NAME'
          : 'AI_ASSISTANT.TESTING.DEFAULT_TEST_RUN_NAME',
      );
      const testRunName = runNames?.[index] || defaultName;
      const llmOptions: Partial<TestLLMOptions> | undefined = run.llmOptions
        ? {
            model: run.llmOptions.model ?? '',
            reasoningEffort: run.llmOptions.reasoningEffort ?? '',
          }
        : undefined;

      return this.testService
        .runTestsV2({
          assistantKey: this.assistantKey,
          testRunName,
          llmOptions,
          voiceConfig: run.voiceConfig,
          testCaseKeys,
          iterations,
          assistantVersionKey,
        })
        .pipe(catchError(() => of<RunTestsV2Response>({ buildUrl: '' })));
    });
  }

  goToGenerateTestCases(testType: TestType): void {
    this.router.navigate(['generate'], {
      relativeTo: this.activatedRoute,
      queryParams: { testType: testType === TestType.TEST_TYPE_VOICE ? 'voice' : 'chat' },
    });
  }

  private refreshTestCases(testType?: TestType): void {
    const table = testType === TestType.TEST_TYPE_VOICE ? this.voiceTable : this.chatTable;
    if (table?.dataSource) {
      table.dataSource.state = {
        ...table.dataSource.state,
        loading: true,
      };

      timer(1500).subscribe(() => {
        table.dataSource.state = {
          ...table.dataSource.state,
          shouldRefetch: true,
          loading: true,
        };
      });
    }
  }
}
